import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAdminRewards, 
  createAdminReward, 
  updateAdminReward, 
  deleteAdminReward, 
  uploadAdminImage
} from '../../api/admin';
import AdminSecureStorage from '../../utils/adminSecureStorage';

interface Reward {
  id: string;
  title: string;
  description: string;
  requiredPoints: number;
  isActive: boolean;
  imageUrl?: string;
  validFrom?: string;
  validUntil?: string;
  quantity: number;
  owner?: {
    id: string;
    businessName: string;
    originalEmail: string;
  };
  redeemedCount: number;
  createdAt: string;
  updatedAt: string;
}


const AdminRewards = () => {
  const navigate = useNavigate();
  const [rewards, setRewards] = useState<Reward[]>([]);
  // category removed
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [filters, setFilters] = useState({
    isActive: ''
  });

  // Helper to update filters and reset to first page
  const updateFilter = (patch: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...patch }));
    setCurrentPage(1);
  };

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requiredPoints: '',
    isActive: true,
    imageUrl: '',
    imageId: '',
    validFrom: '',
    validUntil: '',
    quantity: ''
  });

  // Local search state for reward name; only applied on Enter/Search
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');

  useEffect(() => {
    if (!AdminSecureStorage.isAdminTokenValid()) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate, currentPage, filters, appliedQuery]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rewardsData] = await Promise.all([
        getAdminRewards(currentPage, 20, undefined, 
          filters.isActive ? (filters.isActive === 'true') : undefined)
      ]);

      // Apply client-side name filter by appliedQuery to avoid API calls per keystroke
      const list = rewardsData.data.rewards || [];
      const filteredByName = appliedQuery
        ? list.filter((r: any) => (r.title || '').toLowerCase().includes(appliedQuery.toLowerCase()))
        : list;
      setRewards(filteredByName);
      setTotalPages(
        (rewardsData.data?.pagination?.totalPages as number) ||
        (rewardsData.data?.totalPages as number) ||
        1
      );
      // categories removed
    } catch (error: any) {
      console.error('Error loading data:', error);
      setError(error.response?.data?.error || 'Failed to load data');
      
      if (error.response?.status === 401) {
        AdminSecureStorage.clearAll();
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setImgLoading(true);
      setError('');
      // Check file size (1MB)
      if (selectedFile.size > 1 * 1024 * 1024) {
        setError('File size exceeds 1MB. Please select a smaller file.');
        setImgLoading(false);
        return;
      }
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      try {
        // Get admin token for authentication
        const adminToken = AdminSecureStorage.getAdminToken();
        if (!adminToken) {
          setError('Admin authentication required for file upload');
          setImgLoading(false);
          return;
        }
        
        const response = await uploadAdminImage(formData);
        console.log('Upload response:', response);
        const imageId = (response as any)?.id;
        console.log('Extracted imageId:', imageId);
        if (imageId) {
          const newImageUrl = `${import.meta.env.VITE_API_URL}/public/download/${imageId}`;
          console.log('Setting imageUrl to:', newImageUrl);
          setFormData(prev => ({
            ...prev,
            imageId: imageId,
            imageUrl: newImageUrl
          }));
        }
        setImgLoading(false);
      } catch (error) {
        console.error('Error uploading file:', error);
        setError('Error uploading file. Please try again.');
        setImgLoading(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      imageId: '',
      imageUrl: ''
    }));
    // Clear the file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleCreateReward = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      const rewardData = {
        title: formData.title,
        description: formData.description,
        requiredPoints: parseInt(formData.requiredPoints),
        isActive: formData.isActive,
        imageUrl: formData.imageUrl,
        imageId: formData.imageId,
        validFrom: formData.validFrom ? new Date(formData.validFrom).toISOString() : undefined,
        validUntil: formData.validUntil ? new Date(formData.validUntil).toISOString() : undefined,
        quantity: parseInt(formData.quantity) || 0,
        category: 'General' // Default category since we removed the field
      };
      
      console.log('Creating reward with data:', rewardData);
      console.log('Form data state:', formData);
      const response = await createAdminReward(rewardData);
      console.log('Create reward response:', response);
      setSuccess('Reward created successfully!');
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error: any) {
      console.error('Create reward error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to create reward';
      setError(errorMessage);
    }
  };

  const handleUpdateReward = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingReward) return;
    
    try {
      setError('');
      setSuccess('');
      const rewardData = {
        title: formData.title,
        description: formData.description,
        requiredPoints: parseInt(formData.requiredPoints),
        isActive: formData.isActive,
        imageUrl: formData.imageUrl,
        imageId: formData.imageId,
        validFrom: formData.validFrom ? new Date(formData.validFrom).toISOString() : undefined,
        validUntil: formData.validUntil ? new Date(formData.validUntil).toISOString() : undefined,
        quantity: parseInt(formData.quantity) || 0,
        category: 'General' // Default category since we removed the field
      };
      
      console.log('Updating reward with data:', rewardData);
      console.log('Form data state:', formData);
      const response = await updateAdminReward(editingReward.id, rewardData);
      console.log('Update reward response:', response);
      setSuccess('Reward updated successfully!');
      setEditingReward(null);
      resetForm();
      loadData();
    } catch (error: any) {
      console.error('Update reward error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to update reward';
      setError(errorMessage);
    }
  };

  const handleDeleteReward = async (rewardId: string) => {
    if (!window.confirm('Are you sure you want to delete this reward?')) return;
    
    try {
      setError('');
      setSuccess('');
      await deleteAdminReward(rewardId);
      setSuccess('Reward deleted successfully!');
      loadData();
    } catch (error: any) {
      console.error('Delete reward error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to delete reward';
      setError(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      requiredPoints: '',
      isActive: true,
      imageUrl: '',
      imageId: '',
      validFrom: '',
      validUntil: '',
      quantity: ''
    });
  };

  const openEditModal = (reward: Reward) => {
    setEditingReward(reward);
    
    // Convert ISO dates to datetime-local format
    const formatDateForInput = (isoDate: string | undefined) => {
      if (!isoDate) return '';
      const date = new Date(isoDate);
      // Convert to YYYY-MM-DDTHH:MM format for datetime-local input
      return date.toISOString().slice(0, 16);
    };
    
    setFormData({
      title: reward.title,
      description: reward.description,
      requiredPoints: reward.requiredPoints.toString(),
      isActive: reward.isActive,
      imageUrl: reward.imageUrl || '',
      imageId: '',
      validFrom: formatDateForInput(reward.validFrom),
      validUntil: formatDateForInput(reward.validUntil),
      quantity: reward.quantity?.toString() || '0'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="text-gray-600">Loading rewards...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-8">
            <div className="text-white">
              <h1 className="text-3xl font-bold flex items-center">
                🎁 Manage Rewards
              </h1>
              <p className="text-indigo-100 mt-1">Create and manage reward offerings for your customers</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center px-4 py-2 text-indigo-100 hover:text-white hover:bg-white/10 rounded-lg transition-colors duration-200"
              >
                ← Back to Dashboard
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-6 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 font-medium shadow-lg transition-all duration-200 hover:shadow-xl"
              >
                ➕ Create Reward
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            Filter Rewards
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Status</label>
              <select
                value={filters.isActive}
                onChange={(e) => updateFilter({ isActive: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Reward Name</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search by reward title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setAppliedQuery(searchQuery);
                    }
                  }}
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setAppliedQuery(searchQuery)}
                  className="px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Rewards List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              Rewards <span className="ml-2 text-sm font-normal text-gray-500">({rewards?.length || 0} total)</span>
            </h3>
          </div>
          
          {(rewards || []).length === 0 ? (
            <div className="text-center py-16">
              
              <h3 className="text-lg font-medium text-gray-900 mb-2">No rewards found</h3>
              <p className="text-gray-500 mb-6">Create your first reward to get started with customer loyalty programs.</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors"
              >
                Create First Reward
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-1/3">
                      Reward
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">
                      Points
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                      Quantity
                    </th>
                    
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-56">
                      Owner
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">
                      
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">
                      Redeemed
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-40">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-50">
                  {(rewards || []).map((reward) => (
                    <tr key={reward.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          {reward.imageUrl ? (
                            <img className="h-10 w-10 rounded-lg object-cover mr-3 border border-gray-100" src={reward.imageUrl} alt={reward.title} />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mr-3 text-base">
                              🎁
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 truncate">{reward.title}</div>
                            <div className="text-gray-500 truncate max-w-xs">{reward.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-indigo-600">
                          {reward.requiredPoints.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">
                          {reward.quantity || 0}
                        </div>
                      </td>
                      
                      <td className="px-4 py-3 text-gray-600">
                        {reward.owner?.businessName || reward.owner?.originalEmail || 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block h-2.5 w-2.5 rounded-full ${reward.isActive ? 'bg-emerald-600' : 'bg-red-600'}`}
                          title={reward.isActive ? 'Active' : 'Inactive'}
                          aria-label={reward.isActive ? 'Active' : 'Inactive'}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">
                          {reward.redeemedCount}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openEditModal(reward)}
                            className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteReward(reward.id)}
                            className="text-red-600 hover:text-red-800 font-medium transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/30">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next →
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      Page <span className="font-semibold text-gray-900">{currentPage}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 rounded-l-lg border border-gray-300 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        ← Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-4 py-2 rounded-r-lg border border-gray-300 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success/Error Popup */}
      {(success || error) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              {success && (
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              {error && (
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                {success ? 'Success!' : 'Error'}
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                {success || error}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => {
                    setSuccess('');
                    setError('');
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingReward) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                {editingReward ? 'Edit Reward' : 'Create New Reward'}
              </h3>
              <form onSubmit={editingReward ? handleUpdateReward : handleCreateReward}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 text-left">Title</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 text-left">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        rows={3}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 text-left">Required Points</label>
                      <input
                        type="number"
                        value={formData.requiredPoints}
                        onChange={(e) => setFormData({...formData, requiredPoints: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 text-left">Quantity Available</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.quantity}
                        onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="Enter available quantity"
                        required
                      />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 text-left">Valid From</label>
                      <input
                        type="datetime-local"
                        value={formData.validFrom}
                        onChange={(e) => setFormData({...formData, validFrom: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 text-left">Valid Until</label>
                      <input
                        type="datetime-local"
                        value={formData.validUntil}
                        onChange={(e) => setFormData({...formData, validUntil: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 text-left">Image Upload</label>
                      <div className="mt-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                        />
                        {formData.imageUrl && (
                          <div className="mt-2">
                            <img src={formData.imageUrl} alt="Preview" className="h-20 w-20 object-cover rounded" />
                            <button
                              type="button"
                              onClick={handleRemoveImage}
                              className="mt-1 text-sm text-red-600 hover:text-red-800"
                            >
                              Remove Image
                            </button>
                          </div>
                        )}
                        {imgLoading && (
                          <div className="mt-2 text-sm text-gray-500">Uploading...</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">Active</label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingReward(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    {editingReward ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRewards;
